/**
 * Inline Merchant Selector Component
 * Displays a Select dropdown for switching between merchants
 * Used in pages like Dashboard, Transactions, Payouts, etc.
 */
import { PlusOutlined, ShopOutlined } from '@ant-design/icons';
import { history, useAccess, useIntl, useModel } from '@umijs/max';
import { Select, Space, Spin, Tag } from 'antd';
import React, { useMemo } from 'react';
import type { MerchantInfo } from '@/models/merchant';
import styles from './index.less';

const statusColorMap: Record<string, string> = {
  Active: 'green',
  Pending: 'orange',
  Draft: 'default',
  Suspended: 'red',
  Rejected: 'red',
};

interface InlineMerchantSelectorProps {
  /** Custom style */
  style?: React.CSSProperties;
  /** Custom className */
  className?: string;
  /** Width of the selector */
  width?: number | string;
}

const InlineMerchantSelector: React.FC<InlineMerchantSelectorProps> = ({
  style,
  className,
  width = 280,
}) => {
  const intl = useIntl();
  const access = useAccess();
  const { currentMerchant, merchants, loading, changeMerchant } =
    useModel('merchant');

  const options = useMemo(() => {
    const items: Array<{
      value: number;
      label: string;
      disabled?: boolean;
      optionContent?: React.ReactNode;
    }> = [];

    if (merchants.length === 0) {
      items.push({
        value: -1,
        label: intl.formatMessage({
          id: 'component.inlineMerchantSelector.noMerchants',
          defaultMessage: 'No merchants available',
        }),
        disabled: true,
      });
    } else {
      merchants.forEach((merchant: MerchantInfo) => {
        items.push({
          value: merchant.id,
          label: merchant.partnerName,
          optionContent: (
            <div className={styles.optionContent}>
              <Space>
                <ShopOutlined className={styles.optionIcon} />
                <span className={styles.optionName}>
                  {merchant.partnerName}
                </span>
                {merchant.status !== 'Active' && (
                  <Tag color={statusColorMap[merchant.status] || 'default'}>
                    {merchant.status}
                  </Tag>
                )}
              </Space>
            </div>
          ),
        });
      });
    }

    // Add "Add Merchant" option at the end for users with create permission
    if (access.canCreatePartner) {
      items.push({
        value: -2,
        label: intl.formatMessage({
          id: 'component.inlineMerchantSelector.addMerchant',
          defaultMessage: 'Add New Merchant',
        }),
        optionContent: (
          <div className={styles.addOption}>
            <PlusOutlined style={{ marginRight: 8 }} />
            {intl.formatMessage({
              id: 'component.inlineMerchantSelector.addMerchant',
              defaultMessage: 'Add New Merchant',
            })}
          </div>
        ),
      });
    }

    return items;
  }, [merchants, intl, access.canCreatePartner]);

  const handleChange = (value: number) => {
    if (value === -2) {
      // Navigate to merchants page
      history.push('/merchants');
    } else if (value !== -1) {
      changeMerchant(value);
    }
  };

  if (loading && !currentMerchant) {
    return (
      <div className={`${styles.container} ${className || ''}`} style={style}>
        <Spin size="small" />
      </div>
    );
  }

  if (!currentMerchant) {
    return null;
  }

  return (
    <div className={`${styles.container} ${className || ''}`} style={style}>
      <Select
        value={currentMerchant.id}
        onChange={handleChange}
        style={{ width }}
        className={styles.select}
        optionLabelProp="label"
      >
        {options.map((opt) => (
          <Select.Option
            key={opt.value}
            value={opt.value}
            label={opt.label}
            disabled={opt.disabled}
          >
            {opt.optionContent || opt.label}
          </Select.Option>
        ))}
      </Select>
    </div>
  );
};

export default InlineMerchantSelector;
